// Lib
import _ from "lodash";
import { oc } from "ts-optchain";

// Vue
import Vue from "vue";
import { mapGetters } from "vuex";

// Icons
import { faAngleLeft, faQuestionCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

// Services
import { AddressBookService, ObjectUtil, ShoppingCartService, WarehouseShippingOptionsService } from "@/services";
import { CartService } from "../Services/CartService";

// Mixins
import { countryHelper, inputValidateState, sizeHelper } from "@/components/mixins/";
import { sessionHelper } from "@/components/mixins/sessionHelper";

// Components
import {  DnFormInput, CurrencyText, DnFunctionButton }          from "@/components/Elements";
import { AvailableDealerCodeSelector, PostalAddress, LoadingSpinner } from "@/components";
import { CartContentWrapper } from "@/components/Cart/CartContentWrapper";
import ToolTipPopover from "@/components/Elements/ToolTipPopover.vue";
import { default as CartPromos } from "../Controls/CartPromos/CartPromos.vue";
import { default as CartTotals } from "../Controls/CartTotals/CartTotals.vue";
import { default as FinalDeliveryAddressForm } from "./FinalDeliveryAddressForm/FinalDeliveryAddressForm.vue";

import { constants } from "@/const"
import { FeatureFlagService } from "@/services/FeatureFlagService"

// Types
import {
  AddressType,
  IAccountDto,
  ICustomerAddressDto,
  IDealerInfo,
  IPostalAddress,
  ISelectedWarehouseShippingOptionsDto,
  IShoppingCartListResponseDto,
  IWarehouseThirdPartyAccountDto,
  ShipmentType,
  ShipToType,
} from "@/types";
import { CartStepName, IShoppingCartContext } from "../Types";

export default Vue.extend({
  name: "Step2",
  components: {
    FontAwesomeIcon,
    ToolTipPopover,
    PostalAddress,
    CartContentWrapper,
    CartPromos,
    CartTotals,
    AvailableDealerCodeSelector,
    LoadingSpinner,
    DnFormInput,
    FinalDeliveryAddressForm,
    DnFunctionButton
  },
  mixins: [countryHelper, inputValidateState, sizeHelper, sessionHelper],
  props: {
    context: {
      type: Object,
      default: () => ObjectUtil.createInstance<IShoppingCartContext>(),
    },
    validationErrorMessages: {
      type: Array,
      default: [],
    },
  },
  data: () => ({
    backIcon: faAngleLeft,
    questionIcon: faQuestionCircle,

    // Form
    branch: "",
    poNumber: "",
    commentLine1: "",
    commentLine2: "",
    isWarrantyClaim: false,
    warrantyModel: "",
    warrantySerial: "",
    shipToType: ShipToType.Emergency,

    customerAddressID: 0,
    shipToCustomerName: "",
    shipToCustomerName2: "",
    shipToAddressLine1: "",
    shipToAddressLine2: "",
    shipToCity: "",
    shipToStateProvince: "",
    shipToPostalCode: "",
    shipToCountry: "",
    saveToAddressBook: false,

    finalToCustomerName: "",
    finalToAddressLine1: "",
    finalToAddressLine2: "",
    finalToCity: "",
    finalToStateProvince: "",
    finalToPostalCode: "",
    finalToCountry: "",
    saveFinalTo: false,

    haveLoadedFinalToBranches: false,

    isPromotionalFreight: false,
    isPromatchFreeFreight: false,
    freightTerm: "Prepaid",
    PartsStoreCustomerNumber: "",
    PartsStoreOrderNumber: "",

    // rules
    usesFreightForwarder: false,
    heavyItemWarning: false,

    showEmergencyShipToOption: false,
    emergencyAddress: null,
    accountAddress: null,
    customerAddressList: null,
    freightTermOptions: [],
    allWarehouse: null,
    warehouses: [],
    thirdPartyOptions: null,
    thirdPartyDetails: null,
    warningMessages: [],
    proceedingToStep3: false,
    loadingWarehouses: false,

    savingNewCustomerAddress: false,
    showAddressSaveSuccess: false,
    showAddressSaveFailed: false,
    canShipAirPrevValue: true
  }),
  computed: {
    ...mapGetters(["getContentItem",
      "availableDealerCodes", 
      "IsMcfa", 
      "activeDealerCode", 
      "activeDealer",
      "IsMcfs",
      "IsMcfe",
      "getRecentComponentName",
      "getRecentComponentData",
      "hasPermission",
      "activeAccountId",
      "user"
    ]),
    isEmergencyOrder() {
      return this.context.orderType === "Emergency";
    },
    showComments() {
      return false;
    },
    showOfflineHeader(){
      return this.context.showOfflineToWarehouseMessageHeader === true;
    },
    showPromatchFreeFreight() {
      return (
        this.isEmergencyOrder &&
        oc(this.context as IShoppingCartContext)
          .cartData.Items([])
          .every((x) => x.IsPartsFastPartsFree)
      );
    },
    canShipAir(){
      return oc(this.context as IShoppingCartContext)
          .cartData.Items([])
          .every((x) => x.CanShipAir)
      ;
    },
    showPromotionalFreight() {
      return false;
    },
    showWarrantyDetails() {
      return this.isEmergencyOrder;
    },
    showShipTo() {
      return this.isEmergencyOrder;
    },
    showShippingDetails() {
      return this.isEmergencyOrder;
    },
    showEmergencyAddress() {    
      return this.shipToType === ShipToType.Emergency;
    },
    showAccountAddress() {
      return this.shipToType === ShipToType.Account;
    },
    showCustomerAddress() {
      return this.shipToType === ShipToType.Customer;
    },
    customerAddressOptions() {
      return oc(this.customerAddressList as ICustomerAddressDto[])([]);
    },
    countryOptions() {
      return this.country.map((x: any)  => ({
        value: x.id,
        text: x.label
      }));
    },
    defaultShippingOptionsNotSet() {
      return oc(this.context as IShoppingCartContext)
        .warehouses([])
        .some((x) => (x.value ? false : true));
    },
    validationErrors() {
      var allValidationErrors = this.context?.cartData?.ValidationResults?.ValidationErrors ?? [];
      var filteredErrors = allValidationErrors.filter(e => e.ValidationErrorCode != "RPI60" && e.Reason != 37);
      return filteredErrors;
    },
    hasFedExShipping() {
      return this.warehouses?.some(warehouse => warehouse.carrierDescription === "FedEx") ?? false;
    },
    hasUpsShipping() {
      return this.warehouses?.some(warehouse => warehouse.carrierDescription === "UPS") ?? false;
    },
    overrideExxonMismatchError() {
      let hasExxonMismatchError = this.context?.cartData?.ValidationResults.ValidationErrors.filter(ve => ve.Reason.toString() === '19').length > 0;

      return hasExxonMismatchError && this.context.mixMaxValidationOverride;
    },

    thirdPartyToDisplay() {
      let thirdParties = _.cloneDeep(this.thirdPartyDetails);

      let thirdPartiesToDisplay = [];

      if (this.hasFedExShipping) {
        let fedex = thirdParties.find(function(party) {
          return party.CarrierCode === "FedEx";
        })
        thirdPartiesToDisplay.push(fedex);
      }
      if(this.hasUpsShipping){
        let ups = thirdParties.find(function(party) {
          return party.CarrierCode === "UPS";
        })
        thirdPartiesToDisplay.push(ups);
      }


      var otherThirdPartyWarehouses = thirdParties.filter(w => 
        w.CarrierCode != "UPS" && w.CarrierCode != "FedEx") ?? [];

      thirdPartiesToDisplay = _.concat(thirdPartiesToDisplay, otherThirdPartyWarehouses);

      return thirdPartiesToDisplay;
    },

    isCollect() {
      return this.freightTerm === "Collect";
    },

    thirdPartyOrCollectLabel() {
      return this.isCollect ? " Collect" : " 3rd Party Account";
    },
    erpOfflineHeading(){
      return this.getContentItem("ValidationSummaryHeadingOffline")
    },

    validationSummaryHeading(){
      return this.getContentItem("PleaseFixTheErrorsBelowToContinue")
    },
    showIL01Warehouse(){
      return FeatureFlagService.getFeatureEnabled(constants.FEATURE_NAMES.SHOW_IL_WAREHOUSE);
    }
  },
  watch: {
      "thirdPartyOptions":function(){
        this.context.thirdPartyOptions = this.thirdPartyOptions;
      },
      "warhouses":function(){
        this.updateAllThirdPartyOptions();    
      }
  },
  created() {
    window.scrollTo(0,0)
  },
  mounted() {
    const ctx = this.context as IShoppingCartContext;

    const orderOptions = CartService.getOrCreateOrderOptions(ctx);
    orderOptions.ExxonMixMatchOverride = ctx.mixMaxValidationOverride;
    this.usesFreightForwarder = this.activeDealer?.AccountSalesOrg?.FreightForwarderAgent == "X";
    // Set Initial State

    this.branch = ctx.branch;
    this.warehouses = ctx.warehouses;
    this.shipToType = ctx.orderType;

    this.poNumber = orderOptions.PoNumber;
    this.isWarrantyClaim = orderOptions.IsWarrantyClaim;
    this.warrantyModel = orderOptions.WarrantyModel;
    this.warrantySerial = orderOptions.WarrantySerial;
    this.commentLine1 = orderOptions.CommentsLine1;
    this.commentLine2 = orderOptions.CommentsLine2;
    this.isPromatchFreeFreight = orderOptions.IsPartsFastPartsFree;
    this.freightTerm = "Prepaid"


    this.shipToCustomerName = orderOptions.CustomerAddressName;
    this.shipToCustomerName2 = orderOptions.CustomerAddressName2;
    this.shipToAddressLine1 = orderOptions.CustomerAddress1;
    this.shipToAddressLine2 = orderOptions.CustomerAddress2;
    this.shipToCity = orderOptions.CustomerCity;
    this.shipToStateProvince = orderOptions.CustomerStateProvince;
    this.shipToPostalCode = orderOptions.CustomerPostalCode;
    this.shipToCountry = orderOptions.CustomerCountryCode;
    this.saveToAddressBook = orderOptions.ShouldSaveCustomerAddress;

    this.updateFinalToAddress(orderOptions);

    if (orderOptions.IsEmergencyShipTo) {
      this.shipToType = ShipToType.Emergency;
    } else if (orderOptions.IsShipToCustomer) {
      this.shipToType = ShipToType.Customer;
    } else {
      this.shipToType = ShipToType.Account;
    }

    if(ctx.orderType != "Stock"){
      
      this.loadCustomerAddressList();
      // Parts Store Order Customer
      if(this.getRecentComponentName === "PartsStoreOrderDetails"){
        this.shipToType = ShipToType.Customer;
      }
    }

    if (ctx.orderType === "Emergency") {
      this.updateAllThirdPartyOptions();
      this.loadWarehouseOptions();
      this.changeShipToType();

      if (this.canShipAir === false) {
        this.warningMessages.push(this.getContentItem("ItemsOnOrderNotAllowAirShipping"));
      }
      if (this.heavyItemWarning) {
        this.warningMessages.push(this.getContentItem("HeavyItemAirWarning"));
      }

      this.loadEmergencyAddress();
    }
  },
  methods: {
    navigateToStep(stepName: CartStepName) {
      this.$emit("on-step-change", stepName);
    },
    updateFinalToAddress(finalToAddress){
      this.finalToAccount = finalToAddress.FinalToAccount;
      this.finalToCustomerName = finalToAddress.FinalToCustomerAddressName;
      this.finalToAddressLine1 = finalToAddress.FinalToCustomerAddress1;
      this.finalToAddressLine2 = finalToAddress.FinalToCustomerAddress2;
      this.finalToCity = finalToAddress.FinalToCustomerCity;
      this.finalToStateProvince = finalToAddress.FinalToCustomerStateProvince;
      this.finalToPostalCode = finalToAddress.FinalToCustomerPostalCode;
      this.finalToCountry = finalToAddress.FinalToCustomerCountryCode;
      this.saveFinalTo = finalToAddress.FinalToShouldSaveCustomerAddress;
    },

    goToStep1() {
      this.navigateToStep(CartStepName.Step1);
    },

    manageAddressBook() {
      this.$router.push({name: "AddressBookList"});
    },

    manageShippingOptions() {
      this.$router.push({name: "WarehouseShippingOptions"});
    },

    updateAllThirdPartyOptions(){
      var self = this;

      var thirdPartyDetails = [];
      var ups = this.thirdPartyDetails?.find(o =>  o.CarrierCode === "UPS" ) ?? [];
      var fedex = this.thirdPartyDetails?.find(o =>  o.CarrierCode === "FedEx" ) ?? [];

      if(ups){
        thirdPartyDetails.push(ups);
      }
      if(fedex){
        thirdPartyDetails.push(fedex);
      }
      

      var otherThirdPartyWarehouses = this.warehouses?.filter(w => 
        w.carrierDescription != "UPS" && w.carrierDescription != "FedEx") ?? [];

      
      otherThirdPartyWarehouses.forEach(function (w) {

        let carrierDescription = w.carrierDescription;
        let thirdParty = {};
         if(self.isPromatchFreeFreight){

            
            if(carrierDescription === "Unknown" ||  carrierDescription === "N/A"){
              carrierDescription = w.pfpfValue;
            }

            thirdParty = {
              AccountNumber: "",
              CarrierCode: w.pfpfValue,
              CarrierDescription: carrierDescription
            };
         }
         else{
          if(carrierDescription === "Unknown" ||  carrierDescription === "N/A"){
            carrierDescription = w.value;
          }
          thirdParty = {
            AccountNumber: "",
            CarrierCode: w.value,
            CarrierDescription: carrierDescription
          };
         }

         var thirdPartyAlreadyAdded = thirdPartyDetails.find(t => t.CarrierCode == thirdParty.CarrierCode) ?? null;
         if(thirdPartyAlreadyAdded){
           return;
         }

         thirdParty.AccountNumber = thirdPartyDetails?.find(tpo =>
           tpo.CarrierCode === thirdParty.CarrierCode)?.AccountNumber ?? "";

         thirdPartyDetails.push(thirdParty);  
         
      });
      this.thirdPartyDetails = thirdPartyDetails;
      this.thirdPartyOptions = thirdPartyDetails;
    },

    async changeShipToType() {
      const shipToType = this.shipToType as ShipToType;

      switch (shipToType) {
        case ShipToType.Emergency:
          // This address will be loaded async on mounted.
          break;
        case ShipToType.Account:
          await this.loadAccountShipToAddress();
          break;
        case ShipToType.Customer:
          break;
      }
    },

    async loadEmergencyAddress() {
      if (this.emergencyAddress !== null) {
        return;
      }

      const ctx = this.context as IShoppingCartContext;

      const response = await AddressBookService.GetEmergencyShipToAddress(ctx.accountId, ctx.user.UserId);
      this.emergencyAddress = AddressBookService.ConvertAddressBookAddressToPostalAddress(response);
      this.showEmergencyShipToOption = this.emergencyAddress?.Id !== null && this.emergencyAddress.Id !== 0;
    },

    async loadAccountShipToAddress() {
      const shipToAddress = oc(this.branch as IDealerInfo)
        .Addresses([])
        .find((x) => x.AddressType === AddressType.PartsShipTo);

      let result: IPostalAddress | undefined;

      if (shipToAddress) {
        result = AddressBookService.ConvertAccountAddressToPostalAddress(shipToAddress);
      }

      this.accountAddress = result;

      return Promise.resolve(result);
    },

    async loadCustomerAddressList() {
      var self = this;
      if (this.customerAddressList !== null) {
        return;
      }

      const ctx = this.context as IShoppingCartContext;

      const response = await AddressBookService.GetAddressBookCustomers(
        ctx.accountId,
        ctx.user.UserId,
        "",
        1,
        2000,
        "CustomerName",
        false
      );

      this.customerAddressList = response.Items;

      // if this is a parts store order
      if(this.getRecentComponentName === "PartsStoreOrderDetails") {
        let partsStoreOrderData = this.getRecentComponentData;
        this.PartsStoreCustomerNumber = partsStoreOrderData.partsStoreCustomerNumber;
        this.PartsStoreOrderNumber = partsStoreOrderData.partsStoreOrderNumber;

        // check if the address is already a customer address
        const customer = this.customerAddressList.find(a => 
          self.IsNotNullOrUndefined(a.AddressLine1) &&
          self.IsNotNullOrUndefined(a.AddressLine2) &&
          self.IsNotNullOrUndefined(a.City) &&
          self.IsNotNullOrUndefined(a.PostalCode) &&
          self.IsNotNullOrUndefined(a.StateProvince) &&
          self.IsNotNullOrUndefined(partsStoreOrderData.shippingAddressLine1) &&
          self.IsNotNullOrUndefined(partsStoreOrderData.shippingAddressLine2) &&
          self.IsNotNullOrUndefined(partsStoreOrderData.shippingCity) &&
          self.IsNotNullOrUndefined(partsStoreOrderData.shippingPostalCode) &&
          self.IsNotNullOrUndefined(partsStoreOrderData.shippingState) &&
          a.AddressLine1.toUpperCase().trim() === partsStoreOrderData.shippingAddressLine1.toUpperCase().trim()
          && a.AddressLine2.toUpperCase().trim() === partsStoreOrderData.shippingAddressLine2.toUpperCase().trim()
          && a.City.toUpperCase().trim() === partsStoreOrderData.shippingCity.toUpperCase().trim()
          && a.PostalCode.toUpperCase().trim() === partsStoreOrderData.shippingPostalCode.toUpperCase().trim() 
          && a.StateProvince.toUpperCase().trim() === partsStoreOrderData.shippingState.toUpperCase().trim());
                   
          if(customer) {
            var customerId = customer.AddressId;
            this.customerAddressID = customerId;
          }
          else {
            // not a customer address we have saved so populate the fields
            
            this.freightTerm = partsStoreOrderData.freightTerm ?? "";
            this.shipToAddressLine1 = partsStoreOrderData.shippingAddressLine1 ?? "";
            this.shipToAddressLine2 = partsStoreOrderData.shippingAddressLine2 ?? "";
            this.shipToCity = partsStoreOrderData.shippingCity ?? "";
            this.shipToStateProvince = partsStoreOrderData.shippingState ?? "";
            this.shipToPostalCode = partsStoreOrderData.shippingPostalCode ?? "";

            
            this.poNumber = partsStoreOrderData.poNumber ?? "";

            if(this.poNumber !== ""){
              this.$store.commit("pushTimedMessage", {
                header: "PO Number",
                message: `The PO Number has been set to your part store order number (${this.poNumber}) and can be updated if needed.`,
                sticky: true,
                seconds: 20,
              });
            }

            this.shipToCustomerName = partsStoreOrderData.shippingCompanyName ?? "";

            const shipToCountryToSet = this.country.find(c => c.id === partsStoreOrderData.shippingCountry)        

            if(shipToCountryToSet) {
              this.shipToCountry = shipToCountryToSet.id;
            }
          }
        }
    },

    async customerAddressChanged() {
      const customerAddressID = this.customerAddressID as number;

      if (customerAddressID === 0) {
        return;
      }

      const addressList = this.customerAddressList as ICustomerAddressDto[];

      const address = addressList.find((x) => x.AddressId === customerAddressID);


      this.shipToCustomerName = address?.CustomerName ?? "";
      this.shipToCustomerName2 = address?.CustomerName2 ?? "";
      this.shipToAddressLine1 = address?.AddressLine1 ?? "";
      this.shipToAddressLine2 = address?.AddressLine2 ?? "";
      this.shipToCity = address?.City ?? "";
      this.shipToStateProvince = address?.StateProvince ?? "";
      this.shipToPostalCode = _.trim(address?.PostalCode ?? "");
      this.shipToCountry = address?.Country ?? "";

    },

    addressFormIsValid(){
      // adding some custom validation for these fields because vee-validate displays valid / invalid incorrectly
      // handle missing required fields
      if( !this.shipToCustomerName ||
        !this.shipToAddressLine1 ||
        !this.shipToCity ||
        !this.shipToStateProvince ||
        !this.shipToPostalCode 
      ){
        return false
      }

      // handle input too long
      if( 
        this.shipToCustomerName.length > 30 ||
        this.shipToCustomerName2.length > 30 ||
        this.shipToAddressLine1.length > 25 ||
        this.shipToAddressLine2.length > 25 ||
        this.shipToCity.length > 35 ||
        this.shipToStateProvince.length > 3 || 
        this.shipToPostalCode.length > 10
      ){
        return false
      }

      return true;
    },

    async saveAddress() {
      try {
        this.savingNewCustomerAddress = true;

        const addressDto :ICustomerAddressDto = {
          AccountId: this.activeAccountId,
          AddressId: 0,
          CustomerId: 0,
          CustomerName: this.shipToCustomerName,
          CustomerName2: this.shipToCustomerName2,
          AddressLine1: this.shipToAddressLine1,
          AddressLine2: this.shipToAddressLine2,
          City: this.shipToCity,
          StateProvince: this.shipToStateProvince,
          Country: this.shipToCountry,
          PostalCode: this.shipToPostalCode
        };

        await AddressBookService.SaveCustomerAddress(
          this.activeAccountId,
          this.user.UserId,
          addressDto);

        var self = this;
        self.showAddressSaveSuccess = true;
        setTimeout(function(){
          self.showAddressSaveSuccess = false;
        }, 3000);
      }
      catch{
        var self = this;
        self.showAddressSaveFailed = true;
        setTimeout(function(){
          self.showAddressSaveFailed = false;
        }, 3000);
      } finally {
        this.savingNewCustomerAddress = false;
      }
    },

    loadWarehouseOptions() {
      this.loadFreightOptions();
      this.loadThirdPartyDetails();
      this.loadFacingWarehouse();
      this.updateAllThirdPartyOptions();
    },

    async loadFreightOptions() {
      const ctx = this.context as IShoppingCartContext;

      if (!this.freightTerm) {
        this.freightTerm = await WarehouseShippingOptionsService.LoadFreightTermsDefault(
          ctx.accountId,
          ctx.user.UserId
        );
      }

      const response = await WarehouseShippingOptionsService.LoadFreightTermsList();

      var freightOptions = [];
      if(!this.IsMcfe){
        freightOptions = response.map((term) => ({
          value: term,
          text: term,
        }));
      }else{
        freightOptions = [{
          value: "Prepaid",
          text: "Prepaid",
        }];
      }

      this.freightTermOptions = freightOptions;
    },

    async loadFacingWarehouse() {

      this.loadingWarehouses = true;
      const ctx = this.context as IShoppingCartContext;

      const accountId = ctx.accountId;
      const userId = ctx.user.UserId;
      
      if(this.warehouses == null || ctx.canShipAir !== this.canShipAir){
        const warehouseCodeList = await WarehouseShippingOptionsService.LoadFacingWarehouse(accountId, userId);
        this.context.canShipAir = this.canShipAir;

        if(this.IsMcfa){
          warehouseCodeList.push("GRR");
        }

        const warehouses = [];

        for (const warehouseCode of warehouseCodeList) {
          const warehouseInfo = await this.getWarehouseInfo(accountId, userId, warehouseCode);

          warehouses.push(warehouseInfo);
        }

        this.warehouses = warehouses;
        this.updateAllThirdPartyOptions();

        this.$emit("on-warehouses-changed", this.warehouses);
      }

      const allOptions = _.chain(this.warehouses)
        .map((x) => x.options)
        .flatten()
        .uniqBy((x) => x.value)
        .orderBy((x) => x.text)
        .value();

      this.allWarehouse = {
        code: "ZZ",
        description: "All Warehouses",
        value: "",
        options: allOptions,
      };

      // Parts Store Order Shipping Method
      if(this.getRecentComponentName === "PartsStoreOrderDetails"){
        let partsStoreOrderData = this.getRecentComponentData;

        let partsStoreShipping = partsStoreOrderData.shippingMethod;
        const allWareHouseSelected = this.allWarehouse.options.find(w => w.text === partsStoreShipping);
        
        if(allWareHouseSelected) {
          this.allWarehouse.value = allWareHouseSelected.value;
          this.allWarehouseChanged();

          this.thirdPartyDetails.forEach(p => {
            if(p.CarrierCode === allWareHouseSelected.carrierDescription) {
              p.AccountNumber = partsStoreOrderData.thirdPartyBillingAccountNumber;
            }
          });

          this.thirdPartyOptions.forEach(p => {
            if(p.CarrierCode === allWareHouseSelected.carrierDescription) {
              p.AccountNumber = partsStoreOrderData.thirdPartyBillingAccountNumber;
            }
          });
        }
      }

      this.loadingWarehouses = false;
    },

    async getWarehouseInfo(accountId: number, userId: number, warehouseCode: string): Promise<any> {
      const result = await WarehouseShippingOptionsService.LoadWarehouseShippingOptions(
        accountId,
        userId,
        "",
        warehouseCode,
        true,
        this.canShipAir
      );

      const defaultOption =
        result.find((x) => x.IsDefault) ?? // Find the first default option
        result.find((x) => x.IsShown === true) ?? // Otherwise, find the first shown option
        _.first(result) ?? // Otherwise, use the first option in the array
        ObjectUtil.createInstance<ISelectedWarehouseShippingOptionsDto>({
          IsDefault: true,
          WarehouseDescription: "Default",
          WarehouseCode: warehouseCode,
      }); // If all else fails, create a default object

      const defaultPfpfOption =
        result.find((x) => x.IsExpedited || x.ShippingMethodCode === "U1") ??
        _.first(result) ??
        ObjectUtil.createInstance<ISelectedWarehouseShippingOptionsDto>({
          IsDefault: true,
          WarehouseDescription: "Default",
          WarehouseCode: warehouseCode,
        });

      return {
        code: defaultOption.WarehouseCode,
        description: defaultOption.WarehouseDescription,
        value: defaultOption.ShippingMethodCode,
        pfpfValue: defaultPfpfOption.ShippingMethodCode,
        carrierDescription: defaultOption.CarrierDescription,
        carrierId: defaultOption.CarrierId,
        options: result
          .filter((x) => x.IsShown)
          .map((item) => ({
            value: item.ShippingMethodCode,
            text: `${item.ShippingMethodCode} - ${item.ShippingMethodDescription}`,
            carrierDescription: item.CarrierDescription,
            carrierId: item.CarrierId,
            IsExpedited: item.IsExpedited,
            cutoffTime: this.setCutoffTime(item.CutoffTime)
          })),
      };
    },

    setCutoffTime(cutoffTime: any) {
      // Setting up check to see if we are in daylight savings
      // https://stackoverflow.com/a/11888430
      let currentDateTime = new Date();      
      let jan = new Date(currentDateTime.getFullYear(), 0, 1);
      let jul = new Date(currentDateTime.getFullYear(), 6, 1);
      let dstOffset = Math.max(jan.getTimezoneOffset(), jul.getTimezoneOffset());
      
      let isDstCurrently = currentDateTime.getTimezoneOffset() < dstOffset;

      // Setting up the text that is needed for the drop down
      let fullTime = cutoffTime.substring(cutoffTime.length - 8);
      let hours = parseInt(fullTime.substring(0, 2));
      let minutes = fullTime.substring(3, 5);
      let amPm = hours - 12 < 0 ? "AM" : "PM";
      let hoursConvertTo12Hour = hours - 12 <= 0 ? hours : hours - 12;
      if(hoursConvertTo12Hour === 0){
        hoursConvertTo12Hour = 12;
      }

      let timeSuffix = isDstCurrently ? "CDT" : "CST";    
      let returnTime = `${hoursConvertTo12Hour}:${minutes} ${amPm} ${timeSuffix}`;
      return returnTime;
    },

    getOnlyPfpfWarehouseOptions(warehouses){
      var expeditedWarehouses = warehouses.filter(x => x.IsExpedited || x.value === "U1");
      return expeditedWarehouses;
    },

    async loadThirdPartyDetails() {
      if(!this.context.thirdPartyOptions) {
        const ctx = this.context as IShoppingCartContext;

        const response = await WarehouseShippingOptionsService.LoadThirdPartyDetails(ctx.accountId, ctx.user.UserId);

        this.thirdPartyDetails = _.orderBy(response.Items, (x) => x.CarrierDescription);
        this.thirdPartyOptions = _.cloneDeep(this.thirdPartyDetails);
      }
      else{
        this.thirdPartyOptions = this.context.thirdPartyOptions;
        this.thirdPartyDetails = _.cloneDeep(this.thirdPartyOptions);
      }
    },

    async warehouseOptionChanged(selectedWarehouse: any) {
      this.$emit("on-warehouses-changed", this.warehouses);

      this.updateAllThirdPartyOptions();
      this.setCanProceedToStep3();
      return Promise.resolve({});
    },

    async allWarehouseChanged() {
      const allWarehouse = this.allWarehouse;

      this.warehouses = oc(this.warehouses)([]).map((warehouse) => ({
        ...warehouse,
        value: warehouse.options.some((x) => x.value === allWarehouse.value) ? allWarehouse.value : "",
        pfpfValue: warehouse.options.some((x) => x.value === allWarehouse.value) ? allWarehouse.value : ""
      }));

      await this.warehouseOptionChanged();
    },

    changeOrderType() {
      this.goToStep1();
    },

    loadedFinalToBranches(){
      this.haveLoadedFinalToBranches = true;
    },

    updateWarehouseSelectedOption(warehouse){
      const grWarehouse = _.find(warehouses, (x) => x.code === "GRR");
    },

    async selectedDealerCode(dealer: IDealerInfo) {
      if (!dealer?.PrimaryDealerCode) {
        return;
      }

      // Switch the user's active account/dealer code instead of just tracking the branch locally.
      await this.$store.dispatch("UPDATE_DEALER_CODE", {
        dealerCode: dealer.PrimaryDealerCode,
        sessionVarString: this.getSessionVarString(this.user.UserId),
        refreshAfterProcessing: false,
      });

      this.branch = dealer;

      this.usesFreightForwarder = dealer?.AccountSalesOrg?.FreightForwarderAgent == "X";

      this.loadAccountShipToAddress();

      this.$emit("on-branch-changed", this.branch);
    },

    async proceedToNextStep() {
      this.proceedingToStep3 = true;
      const valid = await this.$validator.validateAll();

      if (!valid) {
        this.proceedingToStep3 = false;

        var firstInvalidFormElement = document.querySelector('.is-invalid');
        if( firstInvalidFormElement !== null){
          firstInvalidFormElement.scrollIntoView();
        }
        return;
      }

      const ctx = this.context as IShoppingCartContext;

      const accountId = ctx.accountId;
      const user = ctx.user;

      const orderOptions = CartService.getOrCreateOrderOptions(ctx);
      orderOptions.AccountId = Number(this.activeDealer.AccountId);
      orderOptions.UserId = user.UserId;
      orderOptions.UserEmailAddress = user.Email;

      orderOptions.PromoCode = oc(ctx).promoCodes([]).join(",");

      orderOptions.ShipmentType = ctx.orderType === "Stock" ? ShipmentType.Stock : ShipmentType.Emergency;

      orderOptions.IsPartsFastPartsFree = this.isPromatchFreeFreight;
      orderOptions.UsePromatchFreeFreight = this.isPromotionalFreight;

      orderOptions.PoNumber = this.poNumber;
      orderOptions.CommentsLine1 = this.commentLine1 ?? "";
      orderOptions.CommentsLine2 = this.commentLine2 ?? "";

      if (this.isWarrantyClaim) {
        orderOptions.IsWarrantyClaim = true;
        orderOptions.ShipmentType = ShipmentType.Warranty;
        orderOptions.WarrantyModel = this.warrantyModel ?? "";
        orderOptions.WarrantySerial = this.warrantySerial ?? "";
      }

      if (this.shipToType === ShipToType.Customer) {
        orderOptions.IsShipToCustomer = true;

        orderOptions.CustomerAddressName = this.shipToCustomerName ?? "";
        orderOptions.CustomerAddressName2 = this.shipToCustomerName2 ?? "";
        orderOptions.CustomerAddress1 = this.shipToAddressLine1 ?? "";
        orderOptions.CustomerAddress2 = this.shipToAddressLine2 ?? "";
        orderOptions.CustomerCity = this.shipToCity ?? "";
        orderOptions.CustomerStateProvince = this.shipToStateProvince ?? "";
        orderOptions.CustomerPostalCode = this.shipToPostalCode ?? "";
        orderOptions.CustomerCountryCode = this.shipToCountry ?? "";
        orderOptions.ShouldSaveCustomerAddress = this.saveToAddressBook;
      } else if (this.shipToType === "Emergency") {
        const emergencyAddress = this.emergencyAddress as IPostalAddress;

        orderOptions.IsEmergencyShipTo = true;
        orderOptions.EmergencyAddressName = emergencyAddress.Name;
        orderOptions.EmergencyAddress1 = emergencyAddress.AddressLine1;
        orderOptions.EmergencyAddress2 = emergencyAddress.AddressLine2;
        orderOptions.EmergencyCity = emergencyAddress.City;
        orderOptions.EmergencyStateProvince = emergencyAddress.StateProvinceCode;
        orderOptions.EmergencyPostalCode = emergencyAddress.PostalCode;
        orderOptions.EmergencyCountryCode = emergencyAddress.CountryCode;
      }


      if (this.usesFreightForwarder) {
        orderOptions.IsFinalToCustomer = true;
        orderOptions.FinalToAccount = this.finalToAccount;
        if (ctx.orderType === "Stock") {
          orderOptions.IsFinalToCustomer = true;
          orderOptions.FinalToCustomerAddressName = this.finalToCustomerName?.substring(0,25) ?? "";
          orderOptions.FinalToCustomerAddress1 = this.finalToAddressLine1?.substring(0,25) ?? "";
          orderOptions.FinalToCustomerAddress2 = this.finalToAddressLine2?.substring(0,25) ?? "";
          orderOptions.FinalToCustomerCity = this.finalToCity?.substring(0,35) ?? "";
          orderOptions.FinalToCustomerStateProvince = this.finalToStateProvince?.substring(0,3) ?? "";
          orderOptions.FinalToCustomerPostalCode = this.finalToPostalCode?.substring(0,10) ?? "";
          orderOptions.FinalToCustomerCountryCode = this.finalToCountry ?? "";
  
          orderOptions.FinalToShouldSaveCustomerAddress = this.saveFinalTo;
        }    
      }

      if (ctx.orderType !== "Stock") {

        orderOptions.FreightTerm = this.freightTerm;

        const thirdPartyOptions = this.thirdPartyOptions as IWarehouseThirdPartyAccountDto[];
        const warehouses = this.warehouses;

        var facingWarehouse = _.first(warehouses) as any;
        const facingWarehouseCode = facingWarehouse?.code;
        const grWarehouse = _.find(warehouses, (x) => x.code === "GRR");
        orderOptions.FacingWarehouseCode = facingWarehouseCode;
             
        if(orderOptions.IsPartsFastPartsFree){
          orderOptions.FacingWarehouseShipVia = facingWarehouse.pfpfValue;
          orderOptions.GrWarehouseShipVia = oc(grWarehouse).pfpfValue("");
        }
        else{
          orderOptions.FacingWarehouseShipVia = facingWarehouse.value;
          orderOptions.GrWarehouseShipVia = oc(grWarehouse).value("");
        }
        
        const alternateWarehouses = _.filter(warehouses, (x) => x.code !== "GRR" && x.code !== facingWarehouseCode);

        orderOptions.AlternateWarehouses = alternateWarehouses.map(function(x){

          var shipViaCode = x.value;
          if(orderOptions.IsPartsFastPartsFree){
            shipViaCode = x.pfpfValue;
          }
          return {
            WarehouseCode: x.code,
            ShipViaCode: shipViaCode ?? "",
          }
        });


        orderOptions.UpsShippingAccount = _.chain(thirdPartyOptions)
          .filter((x) => x.CarrierCode === "UPS")
          .map((x) => x.AccountNumber)
          .first()
          .value();

        orderOptions.FedexShippingAccount = _.chain(thirdPartyOptions)
          .filter((x) => x.CarrierCode === "FedEx")
          .map((x) => x.AccountNumber)
          .first()
          .value();

        const other3rdParties = _.filter(this.thirdPartyDetails, (x) => x.CarrierCode !== "UPS" && x.CarrierCode !== "FedEx");

        if (other3rdParties && other3rdParties.length > 0) {
          orderOptions.IsThirdPartyShipping = true;
          var self = this;
          orderOptions.Other3pShippingAccounts = _.map(other3rdParties, other3rdParty => {
            // TODO: Find 3rd Party Warehouse Info
            let warehouse = {};

            warehouse = _.find(warehouses, (w) => w.code === other3rdParty.CarrierCode);
            if(self.isPromatchFreeFreight){
              warehouse = _.find(warehouses, (w) => w.pfpfValue === other3rdParty.CarrierCode);
            }else{
              warehouse = _.find(warehouses, (w) => w.value === other3rdParty.CarrierCode);
            }
            if(!warehouse){
              return null;
            }

            var warehouseOption = warehouse?.options?.find(w => w.value === other3rdParty.CarrierCode);
            
            
            return {
              ShippingOption: {
                WarehouseCode: warehouse.code,
                ShippingMethodCode: warehouseOption?.value,
                IsShown: true,
                IsDefault: false,
                ShippingMethodDescription: warehouseOption?.carrierDescription,
                WarehouseDescription: warehouse.description,
                CutoffTime: new Date(),
                IsThirdParty: true,
                CarrierId: warehouseOption?.carrierId,
                CarrierDescription: warehouseOption?.carrierDescription,
                IsExpedited: warehouseOption?.IsExpedited,
                IsAir: false,
              },
              AccountNumber: other3rdParty.AccountNumber,
            };
          });
        }
      }

      if(this.PartsStoreCustomerNumber !== ""){
        orderOptions.PartsStoreCustomerNumber = this.PartsStoreCustomerNumber;
      }

      if(this.PartsStoreOrderNumber !== "") {
        orderOptions.PartsStoreOrderNumber = this.PartsStoreOrderNumber;
      }

      this.$emit("order-options-updated", orderOptions);

      const cartData = await ShoppingCartService.GetAllShoppingCartDetails({
        accountId,
        userId: user.UserId,
        userAudiences: ctx.userAudiences,
        orderOptions,
        currencyDecimalDigits: 2,
        isPartsStoreOrder: ctx.isPartsStoreOrder,
      });      

      this.$emit("cart-data-updated", cartData);
      // If SAP or CSD is online and there are validation errors, do not go to next step. 
      if (cartData.ValidationResults.ValidationErrors.length > 0 && !(this.context.isOfflineToSap || this.context.isOfflineToWarehouseSystems)) {
        this.proceedingToStep3 = false;
        window.scrollTo(0,0)
        return;
      }

      if (cartData.ValidationResults.ValidationErrors.length > 1 && (this.context.isOfflineToSap || this.context.isOfflineToWarehouseSystems)) {
        this.proceedingToStep3 = false;
        window.scrollTo(0,0)
        return;
      }

      this.applyPromoCode();
      // No errors? Proceed! 
      if (CartService.canProceedToStep3(ctx, cartData, this.context.isOfflineToSap, this.context.isOfflineToWarehouseSystems)) {
        this.proceedingToStep3 = false;
        this.navigateToStep(CartStepName.Step3);
      }
    },

    setCanProceedToStep3(){
      this.context.canProceedToStep3 = true
    },

    responseContainsErrors(cartData: IShoppingCartListResponseDto): boolean {
      return false;
    },

    applyPromoCode() {
      let promoCodes = this.context.promoCodes ?? [];

      if (!promoCodes) {
        return;
      }

      ShoppingCartService.setPromoCodes(promoCodes);

      this.$emit("promo-codes-changed", promoCodes);
    },

    updateThirdParty(thirdParty: any) {
      this.thirdPartyDetails.forEach(party => {
        if(party.CarrierCode === thirdParty.CarrierCode) {
          party.AccountNumber = thirdParty.AccountNumber;
        }
      });

      this.$emit("update-third-party", this.thirdPartyOptions);
    }
  },
});
